import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { API_URL, authFetch, parseJsonResponse, responseErrorMessage } from "../utils/auth";
import ToolSidebar from "../utils/ToolSideBar";
import OfferWorkspace from "../offers/OfferWorkspace";
import OfferCard from "../offers/OfferCard";
import "../../css/client/ClientMessageDetail.css";
import "../../css/offers/OfferWorkspace.css";

function titleCaseName(value) {
  return (value || "").trim().replace(/[A-Za-z]+/g, (part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase());
}

function personName(person) {
  const firstName = titleCaseName(person?.first_name);
  const lastName = titleCaseName(person?.last_name);
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
  return person?.short_name || person?.name || fullName || person?.full_name || person?.email || "Portal contact";
}

function senderName(message) {
  const firstName = titleCaseName(message?.sender_first_name);
  const lastName = titleCaseName(message?.sender_last_name);
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
  return message?.sender_short_name || message?.sender_name || fullName || message?.sender_full_name || "Portal user";
}

function initialsFor(person) {
  const label = personName(person);
  return label.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "P";
}

function conversationRole(conversation) {
  return conversation?.other_participant_role_label || conversation?.other_participant_role || "Portal contact";
}

function localPathFromUrl(url) {
  if (!url) return "";
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin ? parsed.pathname + parsed.search + parsed.hash : url;
  } catch (_err) {
    return url;
  }
}

function clientOfferPath(url) {
  return localPathFromUrl(url);
}

function invoicePathFromResponse(data) {
  const target = data?.invoice_url || data?.url || (data?.invoice_id ? `/invoices/${data.invoice_id}` : "");
  const path = localPathFromUrl(target);
  return path && path.startsWith("/invoices/") ? path : "";
}
export default function MessageDetailPage({ partner }) {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState(null);
  const storedPortalRole = typeof window !== "undefined" ? localStorage.getItem("portal_role") : "";
  const viewerRole = String(conversation?.viewer_role || storedPortalRole || (partner ? "partner" : "client")).toLowerCase();
  const isClientViewer = viewerRole === "client";
  const canAccessSalesWorkspace = Boolean(conversation?.can_show_sales_workspace && !isClientViewer);
  const showConversationContext = Boolean(!isClientViewer && !canAccessSalesWorkspace);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [paymentGateOffer, setPaymentGateOffer] = useState(null);

  const otherParticipants = useMemo(() => {
    if (!conversation?.participants) return [];
    return conversation.participants.filter((item) => String(item.id) !== String(conversation.viewer_id));
  }, [conversation]);

  const loadConversation = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authFetch(API_URL + "/conversations/" + conversationId + "/");
      const data = await parseJsonResponse(response);
      if (!response.ok) throw new Error(responseErrorMessage(response, data, "Unable to load conversation."));
      if (!data) throw new Error("Unable to load conversation. The server returned an unexpected response.");
      setConversation(data);
      setError("");
    } catch (err) {
      setError(err.message || "Unable to load conversation.");
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    loadConversation();
  }, [loadConversation]);

  useEffect(() => {
    const refreshOnFocus = () => loadConversation();
    window.addEventListener("focus", refreshOnFocus);
    return () => window.removeEventListener("focus", refreshOnFocus);
  }, [loadConversation]);

  function openAcceptanceGate(offer) {
    setPaymentGateOffer(offer);
    setError("");
  }

  async function payOffer(offer) {
    setError("");
    const detailResponse = await authFetch(API_URL + "/offers/" + offer.id + "/");
    const latestOffer = await parseJsonResponse(detailResponse);
    if (!detailResponse.ok) {
      setError(responseErrorMessage(detailResponse, latestOffer, "Unable to refresh offer status."));
      return;
    }
    if (!["accepted", "payment_pending"].includes(latestOffer.status)) {
      openAcceptanceGate(latestOffer || offer);
      return;
    }
    const response = await authFetch(API_URL + "/offers/" + offer.id + "/checkout/", { method: "POST" });
    const data = await parseJsonResponse(response);
    if (!response.ok) {
      if (data?.code === "offer_not_accepted") {
        openAcceptanceGate(latestOffer || offer);
        return;
      }
      setError(responseErrorMessage(response, data, "Unable to start checkout."));
      return;
    }
    const invoicePath = invoicePathFromResponse(data);
    if (!invoicePath) {
      setError("Invoice unavailable.");
      return;
    }
    navigate(invoicePath);
  }

  async function declineOffer(offer) {
    const response = await authFetch(API_URL + "/offers/" + offer.id + "/decline/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note: "Declined from conversation." }),
    });
    const data = await parseJsonResponse(response);
    if (!response.ok) {
      setError(responseErrorMessage(response, data, "Unable to decline offer."));
      return;
    }
    loadConversation();
  }

  async function sendMessage(e) {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    setError("");
    try {
      const response = await authFetch(API_URL + "/conversations/" + conversationId + "/messages/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) throw new Error(responseErrorMessage(response, data, "Unable to send message."));
      if (!data) throw new Error("Unable to send message. The server returned an unexpected response.");
      setConversation(data?.conversation || data);
      setMessage("");
    } catch (err) {
      setError(err.message || "Unable to send message.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="staff-shell">
      <ToolSidebar active="inbox" isPartner={partner} />
      <main className="staff-page message-page">
        <Link to="/inbox" className="message-back">Back to messages</Link>
        {loading ? (
          <section className="staff-card"><p className="staff-empty">Loading conversation...</p></section>
        ) : error ? (
          <section className="staff-card"><p className="staff-empty">{error}</p></section>
        ) : conversation && (
          <div className={"message-sales-layout " + (isClientViewer ? "message-sales-layout--single" : "")} data-testid="message-workspace">
            <div className="message-thread-column">
              <header className="message-detail-header">
                <div className="message-thread-identity">
                  <div>
                    <span>Talking With</span>
                    <h1>{conversation.other_participant_full_name || conversation.other_participant_name || "Messages"}</h1>
                  </div>
                  <span className="role-badge large">{conversationRole(conversation)}</span>
                </div>
                {conversation.sales_project ? <p>{conversation.sales_project.business_name || conversation.sales_project.title}</p> : conversation.subject && <p>{conversation.subject}</p>}
                {otherParticipants.length > 0 && (
                  <div className="message-participants">
                    {otherParticipants.map((participant) => (
                      <div className="participant-chip" key={participant.id}>
                        <div className="identity-avatar small">{initialsFor(participant)}</div>
                        <div>
                          <strong>{personName(participant)}</strong>
                          <span>{participant.role || "Portal contact"}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </header>

              <section className="message-container">
                {conversation.messages.length === 0 ? (
                  <div className="empty-state">No messages yet.</div>
                ) : (
                  conversation.messages.map((msg) => (
                    <article className={"message-bubble " + (msg.is_mine ? "mine" : "theirs") + (msg.message_type === "offer" ? " offer-message" : "")} key={msg.id}>
                      <div className="message-meta">
                        <div>
                          <strong>{senderName(msg)}</strong>
                          <span className="sender-role">{msg.sender_role || "Portal user"}</span>
                        </div>
                        <small>{new Date(msg.created_at).toLocaleString()}</small>
                      </div>
                      {msg.message_type === "offer" && msg.offer ? (
                        <OfferCard offer={msg.offer} onPay={payOffer} onDecline={declineOffer} showClientOfferAction={!partner} />
                      ) : (
                        <p>{msg.body}</p>
                      )}
                    </article>
                  ))
                )}
              </section>

              <form className="message-form" onSubmit={sendMessage}>
                <textarea placeholder="Write a message..." value={message} onChange={(e) => setMessage(e.target.value)} maxLength={5000} />
                <div className="composer-footer">
                  <small>{message.length}/5000</small>
                  <button type="submit" disabled={sending || !message.trim()}>{sending ? "Sending..." : "Send Message"}</button>
                </div>
              </form>
            </div>

            {canAccessSalesWorkspace ? <OfferWorkspace conversationId={conversationId} onOfferSent={loadConversation} /> : showConversationContext ? (
              <aside className="message-context-panel" data-testid="conversation-context-panel">
                <section className="staff-card">
                  <h3>Conversation Context</h3>
                  <p>Sales tools are available only for client or lead conversations tied to a partner project request.</p>
                  <dl>
                    <div><dt>Contact</dt><dd>{conversation.other_participant_full_name || conversation.other_participant_name}</dd></div>
                    <div><dt>Role</dt><dd>{conversationRole(conversation)}</dd></div>
                  </dl>
                </section>
              </aside>
            ) : null}
          </div>
        )}
        {paymentGateOffer && (
          <div className="review-modal-backdrop" role="presentation">
            <section className="review-modal" role="dialog" aria-modal="true" aria-labelledby="payment-gate-title">
              <header className="review-modal-header">
                <h2 id="payment-gate-title">Accept Offer Before Payment</h2>
                <button type="button" onClick={() => setPaymentGateOffer(null)}>Close</button>
              </header>
              <div className="review-modal-body">
                <p>You need to review and accept this offer before making a payment. Open the offer to review the project details, agreement, and terms.</p>
              </div>
              <footer className="review-modal-footer">
                <button type="button" className="review-modal-cancel" onClick={() => setPaymentGateOffer(null)}>Cancel</button>
                <button type="button" className="review-action success" onClick={() => navigate(clientOfferPath(paymentGateOffer.client_offer_url))}>View Offer</button>
              </footer>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
